import { ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, Optional, Output, Self, ViewChild } from '@angular/core';
import { AbstractControl, ControlValueAccessor, FormControl, FormGroup, NgControl } from '@angular/forms';
import { ACCEPTABLE_ALL_MAJOR_FILE_EXTENSIONS, DynamicConfirmByMessageConfirmationType, MAX_FILES_UPLOAD_COUNT, ResponseMessageTypes, RxjsService, SnackbarService } from '@app/shared';
import { ReusablePrimeNGTableFeatureService } from '../../../../../shared/services/reusable-primeng-table-features.service';

const defaultConfig = {
  uploadedFiles: [],
  isASingleFileUpload: true,
  label: 'Upload File',
  firstDivClass: 'col-12',
  extraFirstDivClassNames: '',
  secondDivClass: 'pb-2',
  extraSecondDivClassNames: '',
  placeholder: 'Upload file',
  name: 'File Upload',
  shouldDownloadFileOnClick: false,
  downloadablePropertyName: '',
  disableClass: "",
  disableBtnClass: "",
  fileSizeLimit: "",
}
@Component({
  selector: 'file-upload-custom-control',
  templateUrl: './file-upload-custom-control.component.html'
})

export class FileUploadCustomControlCustomControlComponent implements ControlValueAccessor {
  @Input() isASingleFileUpload = defaultConfig.isASingleFileUpload;
  @Input() acceptableFileExtensions;
  @Input() label = defaultConfig.label;
  @Input() placeholder = defaultConfig.placeholder;
  @Input() name = defaultConfig.name;
  @Input() shouldDownloadFileOnClick = defaultConfig.shouldDownloadFileOnClick;
  @Input() downloadablePropertyName = defaultConfig.downloadablePropertyName;
  @Input() uploadedFiles = defaultConfig.uploadedFiles;
  @Input() firstDivClass = defaultConfig.firstDivClass;
  @Input() extraFirstDivClassNames = defaultConfig.extraFirstDivClassNames;
  @Input() secondDivClass = defaultConfig.secondDivClass;
  @Input() extraSecondDivClassNames = defaultConfig.extraSecondDivClassNames;
  @Input() disableClass = defaultConfig.disableClass;
  @Input() disableBtnClass = defaultConfig.disableBtnClass;
  @Input() fileSizeLimit = defaultConfig.fileSizeLimit;
  @Input() formGroup: FormGroup;
  @Input() isFileUploadRequired: boolean = false;
  @Output() emitUploadedFiles = new EventEmitter();
  selectedFiles = [];
  tempNgControl;
  pscrollbarHeightStyle = { height: '7vh' };
  @ViewChild('fileUploadInput', null) fileUploadInput: ElementRef;

  isRequired;
  constructor(
    @Self() @Optional() public ngControl: NgControl,
    private cdr: ChangeDetectorRef, private rxjsService: RxjsService,
    private snackbarService: SnackbarService, private reusablePrimeNGTableFeatureService: ReusablePrimeNGTableFeatureService
  ) {
    if (ngControl != null) {
      ngControl.valueAccessor = this;
    }
    else {
      this.tempNgControl = new FormControl('');
    }
  }

  ngOnInit() {
    this.uploadedFiles = [];
    this.acceptableFileExtensions = this.acceptableFileExtensions || ACCEPTABLE_ALL_MAJOR_FILE_EXTENSIONS;
    this.isRequired = this.formGroup?.get(this.ngControl?.name)?.validator?.({} as AbstractControl)?.required ? true : false;
    this.formGroup?.get(this.ngControl.name).valueChanges.subscribe((uploadedFile) => {
      if (!uploadedFile) {
        this.ngControl.control.setValue("", { emitEvent: false, onlySelf: true });
        this.ngControl.control.updateValueAndValidity({ emitEvent: false, onlySelf: true });
        this.formGroup.updateValueAndValidity({ emitEvent: false, onlySelf: true });
        this.fileUploadInput.nativeElement.placeholder = "";
      }
    });
  }

  uploadFile(uploadedFile) {
    if (uploadedFile && uploadedFile.length == 0) {
      return;
    }
    const path = uploadedFile[0].name.split('.');
    const extension = "." + path[path.length - 1];
    const duplicatedFile = this.uploadedFiles.find(name => name == uploadedFile[0].name);

    if (uploadedFile && this.isASingleFileUpload == false) {
      if (!this.acceptableFileExtensions.includes(extension)) {
        this.snackbarService.openSnackbar(`Allow to upload the following file formats only - ${this.acceptableFileExtensions}`, ResponseMessageTypes.WARNING);
        return;
      }
      else if (duplicatedFile) {
        this.snackbarService.openSnackbar("File name already exists.Please upload another file.", ResponseMessageTypes.WARNING);
        return;
      }
      else if (uploadedFile.length > MAX_FILES_UPLOAD_COUNT || this.uploadedFiles.length >= MAX_FILES_UPLOAD_COUNT) {
        this.snackbarService.openSnackbar(`You can upload maximum ${MAX_FILES_UPLOAD_COUNT} files`, ResponseMessageTypes.WARNING);
        return;
      }
      else if (this.acceptableFileExtensions.includes(extension)) {
        this.selectedFiles.push(uploadedFile.item(0));
        this.uploadedFiles.push(uploadedFile[0].name);
        if (this.uploadedFiles.length > 2) {
          this.pscrollbarHeightStyle = { height: '15vh' };
        }
      }
      this.placeholder = this.uploadedFiles.join(', ');
    }
    else if (this.isASingleFileUpload) {
      if (!this.acceptableFileExtensions.includes(extension)) {
        this.snackbarService.openSnackbar(`Allow to upload the following file formats only - ${this.acceptableFileExtensions}`, ResponseMessageTypes.WARNING);
        return;
      }
      const allowedSingleDot = /\./g;
      if (uploadedFile[0].name.match(allowedSingleDot)?.length > 1) {
        return this.snackbarService.openSnackbar(`Invalid file name. Please check the file name.`, ResponseMessageTypes.WARNING);
      }

      if (this.fileSizeLimit) {
        var fileSize = uploadedFile[0]?.size; // size in bytes
        var maxSize = +this.fileSizeLimit * 1024 * 1024; // 10MB in bytes
        if (fileSize > maxSize) {
          this.snackbarService.openSnackbar(`File size exceeds the allowed limit of ${this.fileSizeLimit}MB.`, ResponseMessageTypes.WARNING);
          return;
        }
      }

      this.placeholder = uploadedFile[0].name;
      this.emitUploadedFiles.emit(uploadedFile[0]);
    }
    this.emitUploadedFiles.emit(this.selectedFiles);
  }

  removeUploadedFile(index: number) {
    this.reusablePrimeNGTableFeatureService.openDynamicConfirmByMessageDialog(`Are you sure you want to delete this file?`, undefined,
      DynamicConfirmByMessageConfirmationType.DANGER).
      onClose?.subscribe(dialogResult => {
        if (dialogResult) {
          this.uploadedFiles.splice(index, 1);
          this.selectedFiles.splice(index, 1);
          if (this.uploadedFiles.length > 0) {
            this.placeholder = this.uploadedFiles.join(', ');
          }
          else {
            this.placeholder = defaultConfig.placeholder;
          }
          this.emitUploadedFiles.emit(this.selectedFiles);
          this.rxjsService.setFormChangeDetectionProperty(true);
        }
      });
  }

  downloadFile(uploadedFile?) {
    if (this.shouldDownloadFileOnClick) {
      if (this.ngControl.value.includes('https')) {
        window.open(this.ngControl.value, '_blank');
      }
      else if (this.downloadablePropertyName) {
        window.open(uploadedFile[this.downloadablePropertyName], '_blank');
      }
    }
  }

  writeValue(obj: any): void {
  }

  registerOnChange(fn: any): void {

  }

  registerOnTouched(fn: any): void {

  }

  setDisabledState?(isDisabled: boolean): void {

  }

  ngAfterContentInit() {
    this.cdr.detectChanges();
  }
}